import { Injectable, inject } from '@angular/core';
import { AnalyticsService, trackTimedApiCall } from '../../integrations/analytics';
import { AXIOS_INSTANCE } from '../api-client.tokens';
import type {
  PickupLocationsResponse,
  PickupValidateRequest,
  PickupValidateResponse,
} from './models/pickup.model';

/** UI locale code accepted by `Accept-Language` on `GET /pickup/locations`. */
export type PickupLocationsLanguage = 'EN' | 'FR';

/**
 * Pickup API client for schedule-pickup eligibility checks and induction-method lookups.
 * Uses the shared axios instance directly (not `ApiClient`) because `/pickup/locations`
 * requires a custom `Accept-Language` header per call.
 *
 * Calls the live Pickup Lambda only — failures propagate to callers (same pattern as
 * Product Master). Response fixtures for unit tests live under `testing/`.
 */
@Injectable({ providedIn: 'root' })
export class PickupApiService {
  private readonly http = inject(AXIOS_INSTANCE);
  private readonly analytics = inject(AnalyticsService);

  /**
   * Validates pickup eligibility for an address and requested time window via
   * `POST /pickup/validate`.
   * @param flowId Wizard flow id used to bind any returned pickup attestation.
   */
  async validate(request: PickupValidateRequest, flowId: string): Promise<PickupValidateResponse> {
    return trackTimedApiCall(this.analytics, 'getPickupAvailableDates', async () => {
      const response = await this.http.post<PickupValidateResponse>('/pickup/validate', request, {
        headers: {
          'Content-Type': 'application/json',
          'x-flow-id': flowId,
        },
      });
      return response.data;
    });
  }

  /**
   * Returns pickup-from induction location codes via `GET /pickup/locations`.
   * @param language UI locale sent as `Accept-Language` (`EN` or `FR`).
   */
  async getLocations(language: PickupLocationsLanguage): Promise<PickupLocationsResponse> {
    return trackTimedApiCall(this.analytics, 'getPickupLocations', async () => {
      const response = await this.http.get<PickupLocationsResponse>('/pickup/locations', {
        headers: { 'Accept-Language': language },
      });
      return response.data;
    });
  }
}
